import { isSocketDebugNotificationsActivated } from '@functions/functions';
import { EToastVariant } from '@notifications/components/Toast/Toast.enum';
import useAddToast from '@store/toastsStore/hooks/useAddToast';
import { EToastUUID } from '@store/toastsStore/toastsStore.enum';
import { useEffect } from 'react';
import { useSocket } from './useSocket';
import useSocketConnectionState from './useSocketConnectionState';

const useSocketConnectionHandlers = () => {
	const [socket] = useSocket();
	const [, setSocketConnectionState] = useSocketConnectionState();
	const { addToast } = useAddToast();

	useEffect(() => {
		if (socket) {
			// fired when socket connection failed
			socket.on('connect_error', (err: Error) => {
				console.error('Connection error:', err);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						title: 'Connection error',
						message: err.message,
						variant: EToastVariant.error
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					connectionError: new Error('Connection timeout'),
					isConnected: false
				}));
			});

			// fired when socket connection failed due to timeout
			socket.on('connect_timeout', (timeout: number) => {
				console.debug('Connection timeout:', timeout);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						title: 'Connection timeout',
						message: 'Connection timeout',
						variant: EToastVariant.error
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					connectionTimeout: timeout,
					connectionError: new Error('Connection timeout: ' + timeout)
				}));
			});

			// fired when socket is disconnected and reconnection is started
			socket.on('reconnect_attempt', (attempt: number) => {
				console.debug('Reconnection attempt:', attempt);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						variant: EToastVariant.error,
						title: 'Reconnection attempt',
						message: 'Reconnection attempt: ' + attempt
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					reconnectionAttempt: attempt,
					connectionError: new Error('Reconnection attempt: ' + attempt)
				}));
			});

			// fired when socket is disconnected and all reconnection attempts failed
			socket.on('reconnect_failed', (err: Error) => {
				console.error('Reconnection failed', err);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						variant: EToastVariant.error,
						title: 'Reconnection failed',
						message: err.message
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					reconnectionFailed: true,
					connectionError: new Error('Reconnection failed')
				}));
			});

			// fired when socket is successfully reconnected
			socket.on('reconnect', (attempt) => {
				console.debug('Reconnected:', attempt);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						variant: EToastVariant.success,
						title: 'Reconnected',
						message: 'Reconnected after ' + attempt + ' attempts'
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					connectionError: null,
					isConnected: true,
					reconnectionFailed: false,
					reconnectionAttempt: null,
					connectionTimeout: null
				}));
			});

			// fired when socket is successfully connected
			socket.on('connect', () => {
				console.debug('socket connected with session id:', socket.id);

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						variant: EToastVariant.success,
						title: 'Socket Connected',
						message: 'Connected with session id: ' + socket.id
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					isConnected: true,
					connectionError: null,
					reconnectionFailed: false,
					reconnectionAttempt: null,
					connectionTimeout: null
				}));
			});

			// fired when socket is disconnected whatever the reason
			socket.on('disconnect', () => {
				console.debug('Socket disconnected');

				if (isSocketDebugNotificationsActivated()) {
					addToast({
						uuid: EToastUUID.socketConnectionState,
						variant: EToastVariant.error,
						title: 'Socket disconnected',
						message: 'Socket disconnected'
					});
				}

				setSocketConnectionState((prevState) => ({
					...prevState,
					connectionError: null,
					isConnected: false
				}));
			});

			return () => {
				socket.off('connect_error');
				socket.off('connect_timeout');
				socket.off('reconnect_attempt');
				socket.off('reconnect_failed');
				socket.off('reconnect');
				socket.off('connect');
				socket.off('disconnect');
			};
		}
	}, [addToast, setSocketConnectionState, socket]);

	return null;
};

export default useSocketConnectionHandlers;
